import React from "react";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import { useTranslation } from "react-i18next";

type Props = {
  url: string;
  copyLabel: string;
  copiedToast: string;
  size?: "small" | "middle" | "large";
};

async function writeClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "absolute";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

/** US-703: copy button with CheckOutlined feedback + toast. */
export function CopyLinkButton({ url, copyLabel, copiedToast, size = "small" }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const onCopy = async () => {
    try {
      await writeClipboard(url);
      message.success(copiedToast);
      setCopied(true);
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error(t("compliance.share.copyPublicLinkFailed"));
    }
  };

  return (
    <Button
      size={size}
      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
      onClick={() => void onCopy()}
      data-testid="copy-link-button"
      data-copied={copied ? "true" : "false"}
      aria-label={copied ? copiedToast : copyLabel}
    >
      {copied ? t("compliance.share.copied") : copyLabel}
    </Button>
  );
}
